/* ==========================================================================
   Blink Box - Collection subcollection image navigation analytics
   Markup source: sections/subcollection-nav-img.liquid
========================================================================== */
(function () {
  'use strict';

  if (window.__blinkboxSubcollectionNavImgAnalyticsLoaded) return;
  window.__blinkboxSubcollectionNavImgAnalyticsLoaded = true;

  const SELECTORS = {
    root: '[data-subcollection-nav-analytics]',
    nav: '.subcollection-nav-img',
    item: '.subcollection-nav-img__item',
    link: 'a.subcollection-nav-img__item'
  };

  const initialised = new WeakSet();
  const viewed = new WeakSet();
  const viewTimers = new WeakMap();
  const interactionStates = new WeakMap();
  const selectedLinks = new WeakSet();

  function publishShopifyEvent(name, payload) {
    if (typeof window.Shopify?.analytics?.publish !== 'function') return;

    try {
      const result = window.Shopify.analytics.publish(name, payload);
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch (error) {
      // Analytics must never interrupt navigation.
    }
  }

  function sendGoogleEvent(name, params) {
    if (typeof window.gtag !== 'function') return;

    try {
      window.gtag('event', name, params);
    } catch (error) {
      // Google measurement is optional.
    }
  }

  function hasOverflow(nav) {
    return nav.scrollWidth > nav.clientWidth + 2;
  }

  function buildBasePayload(root) {
    const nav = root.querySelector(SELECTORS.nav);

    return {
      source_collection_id: root.dataset.sourceCollectionId || '',
      source_collection_handle: root.dataset.sourceCollectionHandle || '',
      source_collection_name: root.dataset.sourceCollectionTitle || '',
      collection_group: root.dataset.collectionGroup || '',
      source_is_child: root.dataset.sourceIsChild || 'false',
      nav_item_count: nav ? nav.querySelectorAll(SELECTORS.item).length : 0,
      has_overflow: nav ? String(hasOverflow(nav)) : 'false'
    };
  }

  function track(root, eventName, extra = {}) {
    const payload = {
      ...buildBasePayload(root),
      ...extra
    };

    publishShopifyEvent(`blinkbox_${eventName}`, payload);
    sendGoogleEvent(eventName, payload);
  }

  function trackView(root) {
    if (viewed.has(root)) return;

    const timer = viewTimers.get(root);
    if (timer) window.clearTimeout(timer);

    viewTimers.delete(root);
    viewed.add(root);
    track(root, 'subcollection_nav_view');
  }

  function initViewTracking(root, nav) {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (viewed.has(root)) {
          observer.disconnect();
          return;
        }

        if (entry.intersectionRatio >= 0.01) {
          if (viewTimers.has(root)) return;

          viewTimers.set(root, window.setTimeout(() => {
            trackView(root);
            observer.disconnect();
          }, 250));
        } else {
          const timer = viewTimers.get(root);
          if (timer) window.clearTimeout(timer);
          viewTimers.delete(root);
        }
      });
    }, { threshold: [0, 0.01] });

    observer.observe(nav);
  }

  function clearPendingInteraction(state) {
    if (!state) return;

    if (state.pendingTimer) {
      window.clearTimeout(state.pendingTimer);
    }

    state.pendingTimer = null;
    state.pendingMethod = '';
  }

  function setPendingInteraction(root, nav, method) {
    const state = interactionStates.get(root);
    if (!state || state.engaged) return;

    clearPendingInteraction(state);
    state.pendingMethod = method;
    state.scrollAnchor = nav.scrollLeft;
    state.pendingTimer = window.setTimeout(() => {
      clearPendingInteraction(state);
    }, 1200);
  }

  function trackEngage(root, method, direction) {
    const state = interactionStates.get(root);
    if (!state || state.engaged) return;

    state.engaged = true;
    clearPendingInteraction(state);

    if (!viewed.has(root)) trackView(root);

    track(root, 'subcollection_nav_engage', {
      interaction_method: method,
      interaction_direction: direction
    });
  }

  function handleScrollInteraction(root, nav) {
    const state = interactionStates.get(root);
    if (!state || state.engaged || !state.pendingMethod) return;

    const delta = nav.scrollLeft - state.scrollAnchor;
    if (Math.abs(delta) < 12) return;

    trackEngage(
      root,
      state.pendingMethod,
      delta > 0 ? 'next' : 'previous'
    );
  }

  function getDestinationHandle(link) {
    try {
      const url = new URL(link.href, window.location.href);
      const match = url.pathname.match(/\/collections\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      return '';
    }
  }

  function getDestinationName(link) {
    return link.querySelector('.subcollection-nav-img__label')?.textContent?.trim() || '';
  }

  function getDestinationPosition(nav, link) {
    return Array.from(nav.querySelectorAll(SELECTORS.item)).indexOf(link) + 1;
  }

  function trackSelect(root, nav, link) {
    if (!link || selectedLinks.has(link)) return;

    selectedLinks.add(link);

    if (!viewed.has(root)) trackView(root);

    track(root, 'subcollection_nav_select', {
      destination_collection_handle: getDestinationHandle(link),
      destination_collection_name: getDestinationName(link),
      destination_position: getDestinationPosition(nav, link),
      destination_is_parent: link.hasAttribute('data-is-parent') ? 'true' : 'false'
    });
  }

  function initInteractionTracking(root, nav) {
    interactionStates.set(root, {
      engaged: false,
      pendingMethod: '',
      pendingTimer: null,
      scrollAnchor: nav.scrollLeft
    });

    if ('PointerEvent' in window) {
      nav.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch') {
          setPendingInteraction(root, nav, 'touch');
        }
      }, { passive: true });
    } else {
      nav.addEventListener('touchstart', () => {
        setPendingInteraction(root, nav, 'touch');
      }, { passive: true });
    }

    nav.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaX) > 2 || event.shiftKey) {
        setPendingInteraction(root, nav, 'wheel');
      }
    }, { passive: true });

    nav.addEventListener('scroll', () => {
      handleScrollInteraction(root, nav);
    }, { passive: true });

    nav.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;

      const link = event.target.closest(SELECTORS.link);
      if (!link || !nav.contains(link)) return;

      trackSelect(root, nav, link);
    }, true);
  }

  function initComponent(root) {
    if (!root || initialised.has(root)) return;

    const nav = root.querySelector(SELECTORS.nav);
    if (!nav) return;

    initialised.add(root);
    root.dataset.subcollectionNavAnalyticsInitialised = 'true';

    initViewTracking(root, nav);
    initInteractionTracking(root, nav);
  }

  function boot(scope) {
    const container = scope || document;

    if (container instanceof Element && container.matches(SELECTORS.root)) {
      initComponent(container);
    }

    container.querySelectorAll?.(SELECTORS.root).forEach(initComponent);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    boot(event.target);
  });
})();
